import { useEffect, useState } from "react";
import { useQuests } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { QuestCelebrationModal } from "@/components/QuestCelebrationModal";
import type { QuestItemOut } from "@/api/types";

/**
 * Глобальний детект виконаних квестів → celebration-попап в апці (юзер
 * 2026-06-25). Монтується в корені (App) → спрацьовує на будь-якому екрані,
 * коли юзер заходить в апку після виконання квесту (паралельно з уведомленням
 * у боті).
 *
 * Стан «вже показано» зберігаємо в localStorage (set id виконаних квестів).
 * COLD-START: якщо ключа ще немає — сідимо ВСІ поточні виконані як «вже
 * показані» (без попапів), щоб не спамити старими квестами при першому
 * запуску. Попап покаже лише ті, що стануть done ПІСЛЯ цього.
 */
const SEEN_KEY = "celebrated_quests_v1";

function readSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeSeen(set: Set<string>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function QuestCelebrationWatcher() {
  const questsQuery = useQuests();
  const [queue, setQueue] = useState<QuestItemOut[]>([]);

  // Рефетч при поверненні в аппку — піймати квести, виконані поки апка згорнута.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") void questsQuery.refetch();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [questsQuery]);

  useEffect(() => {
    const items = questsQuery.data?.items;
    if (!items) return;
    const doneIds = items.filter((q) => q.is_done).map((q) => q.id);

    // Cold-start: немає ключа → сідимо всі виконані як «показані», без попапів.
    if (localStorage.getItem(SEEN_KEY) === null) {
      writeSeen(new Set(doneIds));
      return;
    }

    const seen = readSeen();
    const fresh = items.filter((q) => q.is_done && !seen.has(q.id));
    if (fresh.length === 0) return;

    // Позначаємо одразу як показані (щоб refetch не дублював), і ставимо в чергу.
    fresh.forEach((q) => seen.add(q.id));
    writeSeen(seen);
    tg()?.HapticFeedback?.notificationOccurred?.("success");
    setQueue((prev) => [...prev, ...fresh]);
  }, [questsQuery.data]);

  if (queue.length === 0) return null;
  return (
    <QuestCelebrationModal
      quest={queue[0]}
      onClose={() => setQueue((prev) => prev.slice(1))}
    />
  );
}
